import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import {
  DvDropdownGridElementParams,
  DvDynamicFormModule,
  DvFormUtils,
  DvSavePanelModule,
  DvTabBase,
  FormOf,
} from '@devinco/ui/legacy';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '@shared/pipes/pipes.module';
import {
  FerryDealDto,
  VehicleTripTypeSetupDto,
} from '@pages/vehicles-page/components/vehicle-tabs/vehicle-trip-type-setup-tab/interfaces';
import { VehicleSetupTabService } from './vehicle-setup-tab.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import {
  combineLatest,
  map,
  Observable,
  Subject,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { SetupTabsSharedService } from '@pages/vehicles-page/services/setup-tabs-shared.service';
import { VehicleDto } from '@pages/vehicles-page/components/vehicles-grid/interfaces';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { VehicleType } from '@shared/constants/vehicle-type';

@Component({
  selector: 'app-vehicle-setup-tab',
  imports: [
    DvDynamicFormModule,
    PipesModule,
    TranslateModule,
    UpperCasePipe,
    DvSavePanelModule,
  ],
  templateUrl: './vehicle-setup-tab.component.html',
  styleUrl: './vehicle-setup-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [VehicleSetupTabService, GridOptionsService],
})
export class VehicleSetupTabComponent
  extends DvTabBase
  implements OnInit, PendingChanges
{
  private tabService = inject(VehicleSetupTabService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private setupTabsSharedService = inject(SetupTabsSharedService);
  private cdRef = inject(ChangeDetectorRef);
  formGroup: FormGroup<FormOf<Partial<VehicleTripTypeSetupDto>>>;
  departmentElements: DvDynamicFormElement[];
  purposeElements: DvDynamicFormElement[];
  privateTaxableElements: DvDynamicFormElement[];
  tripPositionElements: DvDynamicFormElement[];
  ferryDiscountElements: DvDynamicFormElement[];

  private entityChanged$ = new Subject<void>();

  ngOnInit(): void {
    this.departmentElements = this.tabService.departmentElements;
    this.purposeElements = this.tabService.purposeElements;
    this.privateTaxableElements = this.tabService.privateTaxableElements;
    this.tripPositionElements = this.tabService.tripPositionElements;
    this.ferryDiscountElements = this.tabService.ferryDiscountElements;
    this.formGroup = DvFormUtils.createFormGroup([
      ...this.departmentElements,
      ...this.purposeElements,
      ...this.tripPositionElements,
      ...this.privateTaxableElements,
      ...this.ferryDiscountElements,
    ]);
  }

  pendingChangesSave = (): Observable<boolean> => this.saveVehicleSetup();

  hasChanges = (): boolean =>
    (this.formGroup.dirty && this.formGroup.valid) ||
    (this.formGroup.valid && !!this.formGroup.controls.FerryDeals?.dirty);

  override onBaseActions(): void {}
  override onEntityCreate(): void {}

  override onEntityUpdate(): void {
    if (!this.entityId) return;

    this.entityChanged$.next();
    this.formGroup.markAsPristine();
    this.setupTabsSharedService
      .getTabData(this.entityId)
      .pipe(
        takeUntil(this.entityChanged$),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe(([res, vehicleData]) => {
        if (!res.FerryDeals?.length) {
          res.FerryDeals = null;
        }
        (
          this.ferryDiscountElements.find(el => el.options.key === 'FerryDeals')
            ?.options as DvDropdownGridElementParams
        ).selectedValue = res.FerryDeals?.length ? res.FerryDeals : undefined;
        this.formGroup.patchValue(res);
        this.handleAccessRestrictions(vehicleData);
      });
  }

  onSave(): void {
    this.saveVehicleSetup().pipe(take(1)).subscribe();
  }

  private handleAccessRestrictions(vehicleData: Partial<VehicleDto>): void {
    combineLatest([
      this.permissionsService.hasPermissions([
        RolesAndPermissions.TripSettingsTypeSetupEdit,
      ]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.VehicleEditMyPrivate,
      ]),
      this.setupTabsSharedService.isCurrentUsersPersonalVehicle(vehicleData),
    ])
      .pipe(
        takeUntil(this.entityChanged$),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe(
        ([canEdit, canEditMyPrivate, isCurrentUsersPersonalVehicle]) => {
          if (
            (canEdit || (canEditMyPrivate && isCurrentUsersPersonalVehicle)) &&
            vehicleData.ShowRecord
          ) {
            this.formGroup.enable();
            if (!isCurrentUsersPersonalVehicle) {
              this.formGroup.controls.RequirePurposeOnTripTypeChange?.disable();
            }
            this.togglePrivateTaxableFields(
              vehicleData.VehicleTypeDescription!
            );
          } else {
            this.formGroup.disable();
          }
        }
      );
  }

  private saveVehicleSetup(): Observable<boolean> {
    const payload: Partial<VehicleTripTypeSetupDto> =
      DvFormUtils.getDirtyValues(this.formGroup);
    if (payload.FerryDeals?.length) {
      payload.FerryDeals = payload.FerryDeals.map(dealId => ({
        FerryDealID: dealId,
      })) as FerryDealDto[];
    }
    return this.setupTabsSharedService
      .saveVehicleSetup(this.entityId, payload)
      .pipe(
        take(1),
        tap(() => this.formGroup.markAsPristine()),
        map(() => true)
      );
  }

  private togglePrivateTaxableFields(vehicleType: VehicleType): void {
    const { controls } = this.formGroup;
    if (vehicleType === VehicleType.Corporate) {
      controls.PrivateTaxableNotAllowed?.enable();
      controls.PrivateOccasionalNotAllowed?.enable();
    } else {
      controls.PrivateTaxableNotAllowed?.disable();
      controls.PrivateOccasionalNotAllowed?.disable();
    }
    this.cdRef.detectChanges();
  }
}
